import { useCallback, useEffect, useState } from "react";
import {
  MENTORUM_DUYGULARI,
  MENTORUM_DUYGU_ETIKETI,
  mentorumRizaDurumuSemasi,
  etkinlikYanitiKopyasiSemasi,
  type MentorumDuygu as Duygu,
  type MentorumRizaDurumuYaniti,
} from "@koclukpanel/contracts";
import { BosDurum, Buton, Iskelet, Kart, OnayKutusu } from "@koclukpanel/ui";
import { api, ApiHatasi, kendiKaydim } from "./api";
import { MentorumDuygu } from "./MentorumDuygu";
import { MentorumKonu } from "./MentorumKonu";

export interface MentorumKonusu {
  id: string;
  title: string;
  description: string | null;
  authorName: string;
  authorTitle: string;
  media: Array<{ id: string; kind: "VIDEO" | "AUDIO"; title: string }>;
  progress: Array<{ openedAt: string; completedAt: string | null }>;
}

export interface MentorumYaniti {
  id: string;
  activityId: string;
  status: "DRAFT" | "SUBMITTED";
  selectedOptionIds: string[];
  textAnswer: string | null;
  snapshot: unknown;
  submittedAt: string | null;
}

// F-MNT-13/14 — sunucu hatası hassas içerik taşısa bile ekrana ve günlüğe yansıtılmaz.
export function mentorumHataMetni(hata: unknown): string {
  if (hata instanceof ApiHatasi) {
    switch (hata.kod) {
      case "VELI_ONAYI_BEKLIYOR":
        return "Veli onayı bekleniyor.";
      case "MENTORUM_ONAYI_BEKLIYOR":
        return "Mentörüm bilgilendirme onayı bekleniyor.";
      case "VELI_BAGI_DOGRULANMADI":
        return "Veli bağınızın doğrulanması bekleniyor. Kurumla iletişime geçin.";
      case "MENTORUM_ERISIM_YOK":
        return "Mentörüm erişiminiz şu anda açık değil.";
      case "MODUL_KAPALI":
        return "Mentörüm şu anda kullanıma açık değil.";
      case "DUYGU_KAYDI_MEVCUT":
        return "Bugünkü duygu kaydın zaten alındı. Yarın yeniden kaydedebilirsin.";
      case "KONU_TAMAMLANMADI":
        return "Önce açtığın konunun etkinliklerini gönder.";
      case "KONU_ACILMADI":
        return "Etkinliklerden önce konunun videosunu veya ses kaydını aç.";
      case "METIN_DEGISTI":
        return "Onay metni güncellendi. Durumu yenileyip güncel metni yeniden oku.";
      case "YANIT_DEGISTI":
        return "Yanıtın başka bir işlemde değişti. Yeniden yükleyerek devam edebilirsin.";
      case "YANIT_GONDERILMIS":
        return "Bu yanıt gönderilmiş ve değiştirilemez.";
      default:
        if (hata.durum === 403 || hata.durum === 404) return "Bu kayda erişilemiyor.";
    }
  }
  return "İşlem tamamlanamadı. Lütfen tekrar dene.";
}

export function mentorumKapiHatasi(hata: unknown): boolean {
  return hata instanceof ApiHatasi && (hata.durum === 403 || hata.durum === 404);
}

// F-MNT-03 — yalnız sunucunun izinleri menü yüzeyini belirler; rol adı okunmaz.
export function yalnizMentorumMu(izinler: Record<string, string>): boolean {
  return (
    Boolean(izinler["mentorum.icerik.kullan"]) &&
    Object.keys(izinler).every((izin) => izin.startsWith("mentorum."))
  );
}

export function useMentorumMenusu(izinli: boolean) {
  const [durum, setDurum] = useState<"bekliyor" | "acik" | "kapali" | "hata">("bekliyor");
  useEffect(() => {
    if (!izinli) return;
    // ME03-06 — izin geri gelince eski "acik" sonucu yeni yanıt gelene kadar menüyü çizmesin.
    setDurum("bekliyor");
    const iptal = new AbortController();
    const sor = () => {
      void api("/mentorum/erisim-durumum", { signal: iptal.signal, taze: true }).then(
        () => {
          if (!iptal.signal.aborted) setDurum("acik");
        },
        (hata: unknown) => {
          if (!iptal.signal.aborted)
            setDurum(hata instanceof ApiHatasi && hata.kod === "MODUL_KAPALI" ? "kapali" : "hata");
        },
      );
    };
    sor();
    window.addEventListener("focus", sor);
    window.addEventListener("online", sor);
    return () => {
      iptal.abort();
      window.removeEventListener("focus", sor);
      window.removeEventListener("online", sor);
    };
  }, [izinli]);
  return izinli ? durum : "kapali";
}

export function Mentorum() {
  const [veri, setVeri] = useState<{ ogrenciId: string; konular: MentorumKonusu[] } | null>(null);
  const [rizaOgrenciId, setRizaOgrenciId] = useState<string | null>(null);
  const [hata, setHata] = useState<unknown>(null);
  // ME03-01 — odak dönüşü SESSİZ yenilemedir: kapı yeniden sorulur ama açık ekran ve yazılmakta olan taslak
  // yerinde kalır; telefonda uygulama değiştirip dönen öğrenci ana ekrana atılıp yazdığını kaybetmesin.
  // Kapı kapandıysa (rıza geri çekildi, erişim bitti) sessiz yenileme de ekranı kapatır.
  const [yenileme, setYenileme] = useState({ sayi: 0, sessiz: false });
  const [ekran, setEkran] = useState<"ana" | "duygu" | "defter" | MentorumKonusu>("ana");
  useEffect(() => {
    const iptal = new AbortController();
    const sessiz = yenileme.sessiz;
    const yukle = async () => {
      if (!sessiz) {
        setVeri(null);
        setHata(null);
        setRizaOgrenciId(null);
        setEkran("ana");
      }
      try {
        const erisim = await api<{ var: boolean }>("/mentorum/erisim-durumum", {
          signal: iptal.signal,
          taze: true,
        });
        if (!erisim.var) throw new ApiHatasi(403, "MENTORUM_ERISIM_YOK", "");
        const ogrenci = await kendiKaydim();
        if (iptal.signal.aborted) return;
        const riza = mentorumRizaDurumuSemasi.parse(
          await api(`/mentorum/riza/${encodeURIComponent(ogrenci.id)}`, {
            signal: iptal.signal,
            taze: true,
          }),
        );
        if (
          !("ogrenciOnayi" in riza) ||
          !riza.ogrenciOnayi ||
          (riza.veliGerekli && !riza.veliOnayi)
        ) {
          if (iptal.signal.aborted) return;
          setVeri(null);
          setEkran("ana");
          setRizaOgrenciId(ogrenci.id);
          return;
        }
        const konular = await api<MentorumKonusu[]>("/mentorum/icerik", {
          signal: iptal.signal,
          taze: true,
        });
        if (iptal.signal.aborted) return;
        setHata(null);
        setRizaOgrenciId(null);
        setVeri({ ogrenciId: ogrenci.id, konular });
      } catch (sorun) {
        if (iptal.signal.aborted) return;
        if (sessiz && !mentorumKapiHatasi(sorun)) return; // geçici ağ hatası açık ekranı kapatmaz
        setVeri(null);
        setEkran("ana");
        setHata(sorun);
      }
    };
    void yukle();
    const odakta = () => setYenileme((onceki) => ({ sayi: onceki.sayi + 1, sessiz: true }));
    window.addEventListener("focus", odakta);
    return () => {
      iptal.abort();
      window.removeEventListener("focus", odakta);
    };
  }, [yenileme]);
  const yenile = useCallback(
    () => setYenileme((onceki) => ({ sayi: onceki.sayi + 1, sessiz: false })),
    [],
  );
  const kapat = useCallback((sorun: unknown) => {
    setVeri(null);
    setEkran("ana");
    setHata(sorun);
  }, []);
  return (
    <div className="flex min-w-0 flex-col gap-4 p-4 [overflow-wrap:anywhere]">
      <h1 className="text-notr-900 text-xl font-bold">Mentörüm Yanımda</h1>
      {hata ? (
        <BosDurum
          baslik={mentorumHataMetni(hata)}
          eylem={<Buton onClick={yenile}>Tekrar dene</Buton>}
        />
      ) : rizaOgrenciId ? (
        <MentorumRizasi key={rizaOgrenciId} ogrenciId={rizaOgrenciId} onDegisti={yenile} />
      ) : !veri ? (
        <Iskelet satir={3} />
      ) : (
        <>
          {ekran !== "ana" && (
            <Buton gorunum="sessiz" onClick={() => setEkran("ana")}>
              Mentörüm ana sayfasına dön
            </Buton>
          )}
          {ekran === "duygu" ? (
            <MentorumDuygu onKapiHatasi={kapat} />
          ) : ekran === "defter" ? (
            <KisiselDefterim onKapiHatasi={kapat} />
          ) : typeof ekran === "object" ? (
            <MentorumKonu key={ekran.id} konu={ekran} onKapiHatasi={kapat} />
          ) : (
            <>
              <div className="grid min-w-0 gap-3 sm:grid-cols-2">
                <Buton onClick={() => setEkran("duygu")}>Bugün nasıl hissediyorsun?</Buton>
                <Buton gorunum="ikincil" onClick={() => setEkran("defter")}>
                  Kişisel Defterim
                </Buton>
              </div>
              {veri.konular.length === 0 && <BosDurum baslik="Henüz yayınlanmış konu yok" />}
              {veri.konular.map((konu) => (
                <Kart key={konu.id} baslik={konu.title} className="min-w-0">
                  <p className="text-notr-600 text-sm">
                    {konu.authorName} · {konu.authorTitle}
                  </p>
                  {konu.description && (
                    <p className="mt-2 whitespace-pre-wrap">{konu.description}</p>
                  )}
                  <p className="my-2 text-sm">
                    {konu.progress.some((ilerleme) => ilerleme.completedAt !== null)
                      ? "Tamamlandı"
                      : "Etkinliklere devam edebilirsin"}
                  </p>
                  <Buton gorunum="ikincil" onClick={() => setEkran(konu)}>
                    Konuyu aç: {konu.title}
                  </Buton>
                </Kart>
              ))}
              <MentorumRizasi ogrenciId={veri.ogrenciId} onDegisti={yenile} />
            </>
          )}
        </>
      )}
    </div>
  );
}

export function MentorumRizasi({
  ogrenciId,
  veli = false,
  onDegisti,
}: {
  ogrenciId: string;
  veli?: boolean;
  onDegisti?: () => void;
}) {
  const [durum, setDurum] = useState<MentorumRizaDurumuYaniti | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [okundu, setOkundu] = useState(false);
  const [islem, setIslem] = useState(false);
  const [yenileme, setYenileme] = useState(0);
  useEffect(() => {
    const iptal = new AbortController();
    setDurum(null);
    setHata(null);
    setOkundu(false);
    void api(`/mentorum/riza/${encodeURIComponent(ogrenciId)}`, {
      signal: iptal.signal,
      taze: true,
    })
      .then((yanit) => {
        if (!iptal.signal.aborted) setDurum(mentorumRizaDurumuSemasi.parse(yanit));
      })
      .catch((sorun: unknown) => {
        if (!iptal.signal.aborted) setHata(mentorumHataMetni(sorun));
      });
    return () => iptal.abort();
  }, [ogrenciId, yenileme]);
  const tur = veli ? "PARENT_CONSENT" : "STUDENT_NOTICE";
  const belge =
    durum && "belgeler" in durum ? durum.belgeler.find((belge) => belge.kind === tur) : undefined;
  const onayli = durum && "ogrenciOnayi" in durum && (veli ? durum.veliOnayi : durum.ogrenciOnayi);
  const degistir = async (geriCek: boolean) => {
    setIslem(true);
    setHata(null);
    try {
      await api(`/mentorum/riza/${encodeURIComponent(ogrenciId)}${geriCek ? "/geri-cek" : ""}`, {
        yontem: "POST",
        govde: geriCek ? { kind: tur } : { documentId: belge?.id },
      });
      setYenileme((sayi) => sayi + 1);
      onDegisti?.();
    } catch (sorun) {
      setHata(mentorumHataMetni(sorun));
    } finally {
      setIslem(false);
    }
  };
  return (
    <Kart
      baslik={veli ? "Mentörüm veli rızası" : "Mentörüm bilgilendirme onayı"}
      className="min-w-0 [overflow-wrap:anywhere]"
    >
      {hata && <p role="alert">{hata}</p>}
      {!durum ? (
        <>
          <Iskelet satir={2} />
          {hata && <Buton onClick={() => setYenileme((sayi) => sayi + 1)}>Tekrar dene</Buton>}
        </>
      ) : !("ogrenciOnayi" in durum) || (veli && !durum.veliBagiDogrulandi) ? (
        <p role="status">Veli bağınızın doğrulanması bekleniyor. Kurumla iletişime geçin.</p>
      ) : (
        <>
          {!veli && durum.veliGerekli && !durum.veliOnayi && (
            <p role="status">Veli onayı bekleniyor.</p>
          )}
          {veli && !durum.veliGerekli ? (
            <p>Bu öğrenci için veli rızası gerekmiyor.</p>
          ) : (
            <>
              {belge ? (
                <>
                  <h3 className="font-semibold">{belge.title}</h3>
                  <p className="text-notr-600 text-sm">Sürüm: {belge.version}</p>
                  <p className="my-3 whitespace-pre-wrap">{belge.body}</p>
                </>
              ) : (
                <p>[METİN BEKLENİYOR]</p>
              )}
              {onayli ? (
                <>
                  <p role="status">Onayınız kayıtlı.</p>
                  <Buton gorunum="ikincil" disabled={islem} onClick={() => void degistir(true)}>
                    Rızamı geri çek
                  </Buton>
                </>
              ) : (
                <>
                  <OnayKutusu
                    etiket="Metni okudum ve onaylıyorum."
                    checked={okundu}
                    disabled={!belge || islem}
                    onChange={(olay) => setOkundu(olay.target.checked)}
                  />
                  <Buton disabled={!belge || !okundu || islem} onClick={() => void degistir(false)}>
                    Onayla
                  </Buton>
                </>
              )}
            </>
          )}
          <Buton
            gorunum="sessiz"
            disabled={islem}
            onClick={() => {
              setYenileme((sayi) => sayi + 1);
              onDegisti?.();
            }}
          >
            Durumu yenile
          </Buton>
        </>
      )}
    </Kart>
  );
}

export function GonderilmisYanit({ yanit }: { yanit: MentorumYaniti }) {
  const kopya = etkinlikYanitiKopyasiSemasi.safeParse(yanit.snapshot);
  return (
    <Kart baslik={kopya.success ? kopya.data.prompt : "Gönderilmiş yanıt"} className="min-w-0">
      <p className="text-notr-600 text-sm">Gönderildi · Bu yanıt değiştirilemez.</p>
      {kopya.success && (
        <ul>
          {kopya.data.options
            .filter((secenek) => secenek.selected)
            .map((secenek) => (
              <li key={secenek.id} className="mt-2">
                {secenek.text}
              </li>
            ))}
        </ul>
      )}
      {yanit.textAnswer && <p className="mt-2 whitespace-pre-wrap">{yanit.textAnswer}</p>}
    </Kart>
  );
}

function KisiselDefterim({ onKapiHatasi }: { onKapiHatasi: (hata: unknown) => void }) {
  const [veri, setVeri] = useState<{
    duygular: Array<{ id: string; mood: Duygu; day: string }>;
    yanitlar: MentorumYaniti[];
  } | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yenileme, setYenileme] = useState(0);
  useEffect(() => {
    const iptal = new AbortController();
    void api<NonNullable<typeof veri>>("/mentorum/defterim", {
      signal: iptal.signal,
      taze: true,
    }).then(
      (yanit) => {
        if (!iptal.signal.aborted) setVeri(yanit);
      },
      (sorun: unknown) => {
        if (!iptal.signal.aborted) {
          setVeri(null);
          setHata(mentorumHataMetni(sorun));
          if (mentorumKapiHatasi(sorun)) onKapiHatasi(sorun);
        }
      },
    );
    return () => iptal.abort();
  }, [yenileme, onKapiHatasi]);
  return (
    <section className="flex min-w-0 flex-col gap-3">
      <h2 className="text-lg font-semibold">Kişisel Defterim</h2>
      {hata ? (
        <>
          <p role="alert">{hata}</p>
          <Buton
            onClick={() => {
              setHata(null);
              setYenileme((sayi) => sayi + 1);
            }}
          >
            Tekrar dene
          </Buton>
        </>
      ) : !veri ? (
        <Iskelet satir={3} />
      ) : (
        <>
          <Kart baslik="Duygularımın zaman içindeki değişimi">
            {veri.duygular.length === 0 ? (
              <p>Henüz duygu kaydın yok.</p>
            ) : (
              <>
                {/* F-MNT-11 — sabit duygular ayrı satırlardır; sayısal bir iyilik/kötülük puanı türetilmez. */}
                <svg
                  role="img"
                  aria-label="Duygu kayıtlarının zaman çizelgesi"
                  viewBox="0 0 360 250"
                  className="text-marka-700 h-auto w-full"
                >
                  {MENTORUM_DUYGULARI.map((duygu, sira) => (
                    <text key={duygu} x="0" y={25 + sira * 27} fill="currentColor" fontSize="12">
                      {MENTORUM_DUYGU_ETIKETI[duygu]}
                    </text>
                  ))}
                  {[...veri.duygular].reverse().map((kayit, sira) => (
                    <circle
                      key={kayit.id}
                      cx={100 + (sira * 245) / Math.max(1, veri.duygular.length - 1)}
                      cy={21 + MENTORUM_DUYGULARI.indexOf(kayit.mood) * 27}
                      r="4"
                      fill="currentColor"
                    >
                      <title>
                        {tarihMetni(kayit.day)}: {MENTORUM_DUYGU_ETIKETI[kayit.mood]}
                      </title>
                    </circle>
                  ))}
                  <text x="100" y="245" fill="currentColor" fontSize="12">
                    Eskiden → bugüne
                  </text>
                </svg>
                <ul>
                  {veri.duygular.map((kayit) => (
                    <li key={kayit.id}>
                      {tarihMetni(kayit.day)} · {MENTORUM_DUYGU_ETIKETI[kayit.mood]}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Kart>
          {veri.yanitlar.length === 0 && <p>Henüz gönderilmiş yanıtın yok.</p>}
          {veri.yanitlar.map((yanit) => (
            <GonderilmisYanit key={yanit.id} yanit={yanit} />
          ))}
        </>
      )}
    </section>
  );
}

function tarihMetni(tarih: string) {
  return new Date(tarih).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });
}
